// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useMemo, useState } from "react";
import {
  Column,
  Row,
  Button,
  AddBoxIcon,
  EmptyState,
  Heading,
  Select,
  Text,
} from "@hightouch/htui";
import { DecisionEngineFlowConfig } from "@hightouch/lib/customer-data/decision-engine/types";
import { useFlags } from "launchdarkly-react-client-sdk";

import genericPlaceholder from "src/assets/placeholders/generic.svg";
import { SIZES } from "src/ui/box/container-utils";
import { useInsightsContext } from "src/pages/decision-engines/insights/insights-context";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { LinkButton, useSearchParams } from "src/router";

import { useSendInsightCharts } from "./use-send-insight-charts";
import { ChartList } from "./chart-list";
import { ChartBuilder } from "./chart-builder";
import { BUILDER_PARAM, serializeBuilderParam } from "./chart-builder-utils";
import { useLabelResolver } from "./resolve-labels";
import { ChannelConfigurationCard } from "./channel-configuration-card";
import { type ChartConfig, extractMetadataColumns } from "./types";

const LOOKBACK_OPTIONS = [
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
];

export function SendComparison() {
  const { agent } = useInsightsContext();
  const { channels, engine } = useDecisionEngineContext();
  const { decisionEngineMergeTreatmentAndUniform } = useFlags();
  const flowConfig = agent.config as unknown as DecisionEngineFlowConfig;
  const [deletingChartId, setDeletingChartId] = useState<string | null>(null);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [, setSearch] = useSearchParams();

  const hasInsightsData = Boolean(flowConfig.send_insight_views);
  const hasCampaignId = engine.channels.some(
    (ch) => (ch as any).send_event?.attribution?.campaign_id_column,
  );

  const channelTypes = useMemo(
    () => [...new Set(channels.map((ch) => ch.type))],
    [channels],
  );

  const metadataColumns = useMemo(() => {
    const views = flowConfig.send_insight_views;
    const allCharts = [
      ...((views?.fidelity_charts as ChartConfig[] | undefined) ?? []),
      ...((views?.comparison_charts as ChartConfig[] | undefined) ?? []),
    ];
    return extractMetadataColumns(allCharts);
  }, [flowConfig.send_insight_views]);

  const {
    configs,
    chartResultsById,
    isComputing,
    deleteChart,
    saveChart,
    renameChart,
    isSaving,
  } = useSendInsightCharts({
    flowId: agent.id,
    flowConfig,
    tab: "comparison",
    lookbackDays,
    mergeTreatmentAndUniform: decisionEngineMergeTreatmentAndUniform !== false,
  });

  const resolveLabel = useLabelResolver(agent.id);

  const handleDelete = async (chartId: string) => {
    setDeletingChartId(chartId);
    try {
      await deleteChart(chartId);
    } finally {
      setDeletingChartId(null);
    }
  };

  const openBuilder = () => {
    setSearch({ [BUILDER_PARAM]: serializeBuilderParam("comparison") });
  };

  if (!hasInsightsData) {
    return (
      <Column gap={6} p={6} maxWidth={SIZES.page} width="100%" mx="auto">
        <Heading size="md">Send comparison</Heading>
        <EmptyState
          imageUrl={genericPlaceholder}
          title="No send insights data"
          message="Send insights require send events to be linked to your channels. Configure send events on your channels to start comparing send behavior across experiments."
          actions={
            <LinkButton variant="primary" href="/ai/configuration/channels">
              Configure channels
            </LinkButton>
          }
        />
      </Column>
    );
  }

  return (
    <Column gap={6} p={6} maxWidth={SIZES.page} width="100%" mx="auto">
      <Row justify="space-between" align="start">
        <Column gap={1}>
          <Heading size="md">Send comparison</Heading>
          <Text color="text.secondary">
            Compares send volume and patterns across experiment groups to help
            identify differences in how messages are distributed. Results depend
            on your experiment group and channel configurations.
          </Text>
        </Column>
        <Row gap={3} align="center">
          <Select
            width="sm"
            options={LOOKBACK_OPTIONS}
            value={lookbackDays}
            onChange={(val) => {
              if (val) setLookbackDays(val);
            }}
          />
          <Button icon={AddBoxIcon} onClick={openBuilder}>
            Add chart
          </Button>
        </Row>
      </Row>

      <ChannelConfigurationCard channels={engine.channels} />

      <ChartList
        configs={configs}
        chartResultsById={chartResultsById}
        isComputing={isComputing}
        onDelete={handleDelete}
        onRename={renameChart}
        deletingChartId={deletingChartId}
        resolveLabel={resolveLabel}
        hasCampaignId={hasCampaignId}
        flowName={agent.name}
      />

      <ChartBuilder
        onSave={saveChart}
        isSaving={isSaving}
        metadataColumns={metadataColumns}
        tab="comparison"
        channelTypes={channelTypes}
        flowId={agent.id}
        resolveLabel={resolveLabel}
      />
    </Column>
  );
}
